import { useCallback } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  BulkResolveAudiencesEmailProposal,
  BulkResolveAudiencesRowResult,
  BulkResolveAudiencesRowStatus,
} from "@event-planner/shared";
import {
  groupImportSpec,
  type ParsedGroupImportRow,
} from "@event-planner/shared/imports";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../../components/imports/ResolveAndImportDialog";
import {
  type BulkImportResultBucket,
} from "../../../components/BulkImportResultTable";
import {
  bulkCreateAudiences,
  bulkResolveAudiences,
} from "../../../services/audienceService";

interface ExcelGroupImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type AudienceProposal = ProposalRow<
  ParsedGroupImportRow,
  BulkResolveAudiencesRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkCreateAudiences>>;

/**
 * Default include + locked policy. Only clean ('new') rows pre-check;
 * matches_existing / partial / failed all unchecked + locked. Exported
 * for unit testing.
 */
export function defaultIncludeForAudienceStatus(
  status: BulkResolveAudiencesRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "matches_existing":
    case "partial":
    case "failed":
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Bulk Groups (audiences) import with per-row + per-email identity preview.
 *
 * Wraps the shared `ResolveAndImportDialog` shell with audience-specific
 * resolve + write callables. The reviewing phase surfaces ADR-057
 * roster matches per email so organizers see "alice@apex.com is on this
 * event; bob@summit.com is pending; newperson@firm.com is not on the roster"
 * before the actual write.
 *
 * Replaces the prior table-based preview that listed only validation
 * errors — silent dedup-by-email at write time is no longer the only
 * surfacing of identity matches.
 */
export function ExcelGroupImportDialog({
  open,
  onClose,
  eventId,
  onImported,
}: ExcelGroupImportDialogProps) {
  const resolveRows = useCallback(
    async (rows: ParsedGroupImportRow[]) => {
      const response = await bulkResolveAudiences({
        eventId,
        rows: rows.map((r) => ({
          name: r.name,
          description: r.description,
          roles: r.roles,
          memberEmails: r.memberEmails,
        })),
      });
      return response.results;
    },
    [eventId],
  );

  const writeRows = useCallback(
    async (rows: ParsedGroupImportRow[]) => {
      return bulkCreateAudiences({
        eventId,
        rows: rows.map((r) => ({
          name: r.name,
          description: r.description,
          roles: r.roles,
          memberEmails: r.memberEmails,
        })),
      });
    },
    [eventId],
  );

  const renderProposalCard = useCallback(
    (proposal: AudienceProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedGroupImportRow,
      BulkResolveAudiencesRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Groups from Excel"
      entityNoun={{ singular: "Group", plural: "Groups" }}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) with one row per group. Required:{" "}
          <strong>name</strong>. Member emails are resolved server-side
          against this event's roster; you'll see who each email matches before
          anything is saved.
        </>
      }
      testIdSlug="group"
      spec={groupImportSpec}
      templateFileName="group-import-template.xlsx"
      sheetName="Groups"
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForAudienceStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildAudienceBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.summary.created > 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-row proposal card (audience-specific layout)
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: AudienceProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;

  // Format-invalid row (no resolution data).
  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {parsed.data.name || <em>(missing name)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Stack spacing={1} flex={1} minWidth={0}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {resolution.typedName}
            </Typography>
            <StatusChip status={resolution.status} />
            {resolution.proposal?.matchedExistingAudienceId && (
              <Typography variant="caption" color="text.secondary">
                (existing group)
              </Typography>
            )}
          </Stack>

          {parsed.data.roles.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Roles:
              </Typography>
              {parsed.data.roles.map((r) => (
                <Chip key={r} label={r} size="small" variant="outlined" />
              ))}
            </Stack>
          )}

          {resolution.proposal && resolution.proposal.emailProposals.length > 0 && (
            <>
              <Divider />
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Members ({resolution.proposal.emailProposals.length})
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {resolution.proposal.emailProposals.map((p, i) => (
                    <EmailChip key={`${p.email}-${i}`} proposal={p} />
                  ))}
                </Stack>
              </Stack>
            </>
          )}

          {resolution.message && (
            <Typography variant="caption" color="warning.main">
              {resolution.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function EmailChip({ proposal }: { proposal: BulkResolveAudiencesEmailProposal }) {
  const color =
    proposal.source === "existing_user"
      ? "success"
      : proposal.source === "existing_contact"
        ? "success"
        : "error";
  const label =
    proposal.displayName && proposal.displayName !== proposal.email
      ? proposal.displayName
      : proposal.email;
  const tooltipParts: string[] = [proposal.email];
  if (proposal.source === "existing_user") {
    tooltipParts.push("Claimed roster person");
    if (proposal.priorEventNames && proposal.priorEventNames.length > 0) {
      tooltipParts.push(
        `Prior events: ${proposal.priorEventNames.join(", ")}`,
      );
    }
  } else if (proposal.source === "existing_contact") {
    tooltipParts.push("Pending roster person");
    if (proposal.priorEventNames && proposal.priorEventNames.length > 0) {
      tooltipParts.push(
        `Prior events: ${proposal.priorEventNames.join(", ")}`,
      );
    }
  } else {
    tooltipParts.push(
      "Not on this event roster. Add them via the Users page first.",
    );
  }
  return (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltipParts.join("\n")}</Box>}>
      <Chip
        size="small"
        avatar={
          proposal.source !== "no_match" && proposal.photoURL ? (
            <Avatar src={proposal.photoURL} />
          ) : undefined
        }
        label={label}
        color={color}
        variant={proposal.source === "no_match" ? "outlined" : "filled"}
      />
    </Tooltip>
  );
}

function StatusChip({ status }: { status: BulkResolveAudiencesRowStatus }) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "matches_existing":
      return <Chip size="small" label="Matches existing group" color="info" />;
    case "partial":
      return <Chip size="small" label="Some emails unmatched" color="warning" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

// ---------------------------------------------------------------------------
// Done-phase result bucketing
// ---------------------------------------------------------------------------

interface ResultRow {
  lineNumber: number;
  name: string;
  status: "success" | "duplicate" | "unresolved" | "skipped" | "failed" | "invalid";
  message: string | null;
}

function buildAudienceBuckets(
  writeResult: WriteResult | null,
  includedProposals: AudienceProposal[],
  skippedProposals: AudienceProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        name: p.parsed.data.name,
        status: "failed" as const,
        message: "Skipped at write phase",
      };
    }
    return {
      lineNumber: p.lineNumber,
      name: p.parsed.data.name,
      status:
        r.status === "created"
          ? "success"
          : r.status === "duplicate_name"
            ? "duplicate"
            : r.status === "unresolved_emails"
              ? "unresolved"
              : "failed",
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        name: p.parsed.data.name || "(no name)",
        status: "invalid",
        message: p.parsed.message ?? "Format error",
      };
    }
    return {
      lineNumber: p.lineNumber,
      name: p.resolution.typedName,
      status: "skipped",
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );
  const success = all.filter((r) => r.status === "success");
  const duplicate = all.filter((r) => r.status === "duplicate");
  const unresolved = all.filter((r) => r.status === "unresolved");
  const skipped = all.filter((r) => r.status === "skipped");
  const failed = all.filter(
    (r) => r.status === "failed" || r.status === "invalid",
  );
  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      rows: success.map((r) => ({
        id: `s-${r.lineNumber}`,
        primary: r.name,
        secondary: `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "duplicate",
      label: "Already Exists",
      color: "warning",
      description: "A group with this name already exists in the event.",
      rows: duplicate.map((r) => ({
        id: `d-${r.lineNumber}`,
        primary: r.name,
        secondary: `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "unresolved",
      label: "Unresolved Emails",
      color: "warning",
      description:
        "One or more emails aren't on this event roster. Add the people first via the Users page, then re-import.",
      rows: unresolved.map((r) => ({
        id: `u-${r.lineNumber}`,
        primary: r.name,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unresolved"}`,
      })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      description:
        "Rows the organizer chose not to import (matches existing group, partial-match emails, or otherwise locked).",
      rows: skipped.map((r) => ({
        id: `k-${r.lineNumber}`,
        primary: r.name,
        secondary: `Row ${r.lineNumber}${r.message ? `: ${r.message}` : ""}`,
      })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: failed.map((r) => ({
        id: `f-${r.lineNumber}`,
        primary: r.name,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
      })),
    },
  ];
}
